/**
 * BookingDetailsCard Component
 * VOY-229: Display detailed hotel and flight booking information in checkout sidebar
 */

import { useMemo } from 'react'
import { format, differenceInDays, parseISO } from 'date-fns'
import { 
  Plane, 
  Hotel, 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  Star, 
  Coffee, 
  ArrowRight,
  Luggage
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import type { CartItem } from '@/types/cart'
import type { Hotel as HotelType } from '@/types/travel'
import { formatMealPlan, isDisplayableMealPlan } from '@/lib/utils'

interface BookingDetailsCardProps {
  items: CartItem[]
}

// Format duration string (e.g., "5h 30m")
function formatDuration(duration: string): string {
  if (!duration) return ''
  // Handle ISO duration format (PT5H30M)
  if (duration.startsWith('PT')) {
    const hours = duration.match(/(\d+)H/)?.[1] || '0'
    const minutes = duration.match(/(\d+)M/)?.[1] || '0'
    return `${hours}h ${minutes}m`
  }
  return duration
}

// Format time for display (e.g., "2:30 PM")
function formatTime(dateString: string): string {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'h:mm a')
  } catch {
    return ''
  }
}

// Format date for display (e.g., "Dec 15, 2024")
function formatDate(dateString: string): string {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'MMM d, yyyy')
  } catch {
    return dateString
  }
}

// Flight Booking Card Component
function FlightBookingCard({ item }: { item: CartItem }) {
  const flightData = item.item as any
  const slices = flightData.slices || []

  // For single-slice flights (one-way), the flight data IS the slice —
  // there is no slices[] array. Fall through to top-level fields so the
  // card shows real IATA codes instead of "DEP" / "ARR" placeholders.
  const isRoundTrip = slices.length > 1
  const outboundSlice = slices[0] || flightData
  const returnSlice = slices[1]

  const passengerCount = useMemo(() => {
    const passengers = flightData.passengers || { adults: 1, children: 0 }
    return (passengers.adults || 1) + (passengers.children || 0)
  }, [flightData])

  const totalStops = useMemo(() => {
    return outboundSlice?.stops || (outboundSlice?.segments?.length ? outboundSlice.segments.length - 1 : 0)
  }, [outboundSlice])

  // Get airline info — try slice-level, then top-level flight fields
  const airlineName = outboundSlice?.airline || outboundSlice?.segments?.[0]?.airline || flightData.airline || 'Airline'
  const airlineLogo = flightData.ownerLogoSymbolUrl || outboundSlice?.ownerLogoSymbolUrl
  
  return (
    <Card className="overflow-hidden border-blue-100 dark:border-blue-900/30">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Plane className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-sm">Flight</h4>
            <p className="text-xs text-muted-foreground">
              {isRoundTrip ? 'Round Trip' : 'One Way'}
            </p>
          </div>
          <Badge variant="secondary" className="ml-auto text-xs">
            <Users className="h-3 w-3 mr-1" />
            {passengerCount} passenger{passengerCount !== 1 ? 's' : ''}
          </Badge>
        </div>
        
        <Separator />
        
        {/* Outbound Flight */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {outboundSlice?.segments?.[0]?.departureTime 
              ? formatDate(outboundSlice.segments[0].departureTime)
              : 'Outbound'}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-center">
              <p className="text-lg font-bold">{outboundSlice?.origin || 'DEP'}</p>
              <p className="text-xs text-muted-foreground">
                {outboundSlice?.segments?.[0]?.departureTime 
                  ? formatTime(outboundSlice.segments[0].departureTime)
                  : ''}
              </p>
            </div>
            
            <div className="flex-1 px-4">
              <div className="flex items-center justify-center gap-2">
                <div className="h-[2px] flex-1 bg-gray-200 dark:bg-gray-700" />
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {totalStops === 0 ? 'Direct' : `${totalStops} stop${totalStops > 1 ? 's' : ''}`}
                </div>
                <div className="h-[2px] flex-1 bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="flex items-center justify-center mt-1">
                <Clock className="h-3 w-3 text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground">
                  {formatDuration(outboundSlice?.duration || '')}
                </span>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-lg font-bold">{outboundSlice?.destination || 'ARR'}</p>
              <p className="text-xs text-muted-foreground">
                {outboundSlice?.segments?.[outboundSlice.segments.length - 1]?.arrivalTime 
                  ? formatTime(outboundSlice.segments[outboundSlice.segments.length - 1].arrivalTime)
                  : ''}
              </p>
            </div>
          </div>
          
          {/* Airline Info */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {airlineLogo ? (
              <img src={airlineLogo} alt={airlineName} className="h-4 w-4" />
            ) : (
              <Plane className="h-3 w-3" />
            )}
            <span>{airlineName}</span>
            {outboundSlice?.cabinClass && (
              <>
                <span>•</span>
                <span className="capitalize">{outboundSlice.cabinClass}</span>
              </>
            )}
          </div>
        </div>
        
        {/* Return Flight */}
        {returnSlice && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                {returnSlice?.segments?.[0]?.departureTime 
                  ? formatDate(returnSlice.segments[0].departureTime)
                  : 'Return'}
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="text-lg font-bold">{returnSlice?.origin || 'DEP'}</p>
                  <p className="text-xs text-muted-foreground">
                    {returnSlice?.segments?.[0]?.departureTime 
                      ? formatTime(returnSlice.segments[0].departureTime)
                      : ''}
                  </p>
                </div>
                
                <div className="flex-1 px-4">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-[2px] flex-1 bg-gray-200 dark:bg-gray-700" />
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <div className="h-[2px] flex-1 bg-gray-200 dark:bg-gray-700" />
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-lg font-bold">{returnSlice?.destination || 'ARR'}</p>
                  <p className="text-xs text-muted-foreground">
                    {returnSlice?.segments?.[returnSlice.segments.length - 1]?.arrivalTime 
                      ? formatTime(returnSlice.segments[returnSlice.segments.length - 1].arrivalTime)
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Price */}
        <Separator />
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Flight total</span>
          <span className="font-semibold">${item.price.toFixed(2)}</span>
        </div>
      </CardContent>
    </Card>
  )
}

// Hotel Booking Card Component
function HotelBookingCard({ item }: { item: CartItem }) {
  const hotelData = item.item as HotelType
  const selectedRoom = (hotelData as any).selectedRoom
  const raw = hotelData as any

  // Extract dates from hotel data or room selection (support checkin/checkIn/check_in etc.)
  const checkIn = raw.checkin ?? raw.checkIn ?? raw.check_in
    ?? selectedRoom?.checkin ?? selectedRoom?.checkIn ?? selectedRoom?.check_in
    ?? hotelData.checkIn ?? selectedRoom?.checkIn
  const checkOut = raw.checkout ?? raw.checkOut ?? raw.check_out
    ?? selectedRoom?.checkout ?? selectedRoom?.checkOut ?? selectedRoom?.check_out
    ?? hotelData.checkOut ?? selectedRoom?.checkOut

  // Use nights from item if present (e.g. from search), else compute from dates
  const nights = useMemo(() => {
    const explicitNights = (hotelData as any).nights ?? selectedRoom?.nights
    if (typeof explicitNights === 'number' && explicitNights >= 1) return explicitNights
    if (checkIn && checkOut) {
      try {
        const startDate = typeof checkIn === 'string' ? parseISO(checkIn) : checkIn
        const endDate = typeof checkOut === 'string' ? parseISO(checkOut) : checkOut
        const d = differenceInDays(endDate, startDate)
        return d >= 1 ? d : 1
      } catch {
        return 1
      }
    }
    return 1
  }, [checkIn, checkOut, (hotelData as any).nights, selectedRoom?.nights])
  
  // Get first valid image
  const imageUrl = useMemo(() => {
    const images = hotelData.images || []
    return images.find(img => img && typeof img === 'string' && img.startsWith('http')) 
      || 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=400&q=80'
  }, [hotelData.images])
  
  return (
    <Card className="overflow-hidden border-emerald-100 dark:border-emerald-900/30">
      <div className="relative h-32 bg-gray-100 dark:bg-zinc-800">
        <img
          src={imageUrl}
          alt={hotelData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 left-2">
          <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
            <Hotel className="h-3 w-3 mr-1" />
            Hotel
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-4 space-y-3">
        {/* Hotel Name & Rating */}
        <div>
          <h4 className="font-medium text-sm line-clamp-1">{hotelData.name}</h4>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center">
              {Array.from({ length: hotelData.starRating || 4 }).map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            {hotelData.rating && (
              <span className="text-xs text-muted-foreground">
                {hotelData.rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
        
        {/* Location */}
        {hotelData.location && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{hotelData.location}</span>
          </div>
        )}
        
        <Separator />
        
        {/* Dates */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-muted-foreground">Check-in</p>
            <p className="font-medium">
              {checkIn ? formatDate(String(checkIn)) : 'TBD'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Check-out</p>
            <p className="font-medium">
              {checkOut ? formatDate(String(checkOut)) : 'TBD'}
            </p>
          </div>
        </div>
        
        {/* Room Info */}
        {selectedRoom && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <Luggage className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">{selectedRoom.name || 'Standard Room'}</span>
              </div>
              {selectedRoom.mealPlan && isDisplayableMealPlan(selectedRoom.mealPlan) && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Coffee className="h-3 w-3" />
                  <span>{formatMealPlan(selectedRoom.mealPlan)}</span>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Price */}
        <Separator />
        <div className="space-y-1">
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>${(item.price / nights).toFixed(0)} × {nights} night{nights !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Hotel total</span>
            <span className="font-semibold">${item.price.toFixed(2)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Main BookingDetailsCard Component
export function BookingDetailsCard({ items }: BookingDetailsCardProps) {
  const flightItems = items.filter(item => item.type === 'flight')
  const hotelItems = items.filter(item => item.type === 'hotel')
  
  if (items.length === 0) {
    return null
  }
  
  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground">Your Booking</h3>
      
      {flightItems.map((item, index) => (
        <FlightBookingCard key={`flight-${index}`} item={item} />
      ))}
      
      {hotelItems.map((item, index) => (
        <HotelBookingCard key={`hotel-${index}`} item={item} />
      ))}
    </div>
  )
}


